import api, { tokenManager } from './api';

export interface ApiEnvelope<T> {
    success: boolean;
    data: T;
}

// Types
export interface SendOTPRequest {
    phone_number: string;
}

export interface SendOTPResponse {
    message: string;
    message_fa: string;
    expires_in: number;
}

export interface VerifyOTPRequest {
    phone_number: string;
    code: string;
}

export interface User {
    id: string;
    phone_number: string;
    full_name: string;
    role: 'customer' | 'wholesale' | 'retailer' | 'goldsmith' | 'admin' | 'super_admin';
}

export interface VerifyOTPResponse {
    access_token: string;
    refresh_token: string;
    access_token_expires_at: number;
    refresh_token_expires_at: number;
    user: User;
}

export interface UserProfile {
    id: string;
    phone_number: string;
    full_name: string;
    national_id?: string;
    email?: string;
    business_name?: string;
    business_license?: string;
    address?: string;
    city?: string;
    province?: string;
    postal_code?: string;
    role: string;
    status: string;
    labor_fee_percent: number;
    agency_profit_percent: number;
    discount_percent: number;
    can_see_prices: boolean;
    can_see_labor: boolean;
    can_see_tax: boolean;
    can_see_stone_details: boolean;
    created_at: string;
}

// Auth Service
export const authService = {
    /**
     * ارسال کد تایید به شماره موبایل
     */
    sendOTP: async (phoneNumber: string): Promise<SendOTPResponse> => {
        const response = await api.post<ApiEnvelope<SendOTPResponse>>('/auth/send-otp', {
            phone_number: phoneNumber,
        });
        return response.data.data;
    },

    /**
     * تایید کد OTP و دریافت توکن
     */
    verifyOTP: async (phoneNumber: string, code: string): Promise<VerifyOTPResponse> => {
        const response = await api.post<ApiEnvelope<VerifyOTPResponse>>('/auth/verify-otp', {
            phone_number: phoneNumber,
            code: code,
        });
        return response.data.data;
    },

    /**
     * دریافت پروفایل کاربر
     */
    getProfile: async (): Promise<UserProfile> => {
        const response = await api.get<UserProfile>('/users/me');
        return response.data;
    },

    /**
     * بروزرسانی پروفایل
     */
    updateProfile: async (data: Partial<UserProfile>): Promise<UserProfile> => {
        const response = await api.put<UserProfile>('/users/me', data);
        return response.data;
    },

    /**
     * خروج از حساب
     */
    logout: async (): Promise<void> => {
        try {
            await api.post('/auth/logout');
        } finally {
            tokenManager.clearTokens();
        }
    },

    /**
     * بررسی وضعیت احراز هویت
     */
    isAuthenticated: (): boolean => {
        return tokenManager.hasTokens();
    },
};

export default authService;

export interface AdminUser {
    id: string;
    phone_number: string;
    full_name: string;
    role: 'customer' | 'wholesale' | 'retailer' | 'goldsmith' | 'admin' | 'super_admin';
    status: string;
    created_at: string;
    can_see_prices: boolean;
    can_see_labor: boolean;
    can_see_tax: boolean;
    can_see_stone_details: boolean;
    labor_fee_percent: number;
    agency_profit_percent: number;
    discount_percent: number;
}

export interface ListUsersFilters {
    search?: string;
    role?: AdminUser['role'] | '';
    status?: string;
    page?: number;
    page_size?: number;
}

export interface AdminUsersListResponse {
    users: AdminUser[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
}

export const adminUsersService = {
    listUsers: async (filters: ListUsersFilters = {}): Promise<AdminUsersListResponse> => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, String(value));
            }
        });
        const response = await api.get<AdminUsersListResponse>(`/admin/users?${params.toString()}`);
        return response.data;
    },
    getUser: async (id: string): Promise<AdminUser> => {
        const response = await api.get<AdminUser>(`/admin/users/${id}`);
        return response.data;
    },
    createUser: async (data: Partial<AdminUser>): Promise<AdminUser> => {
        const response = await api.post<AdminUser>('/admin/users', data);
        return response.data;
    },
    updateUser: async (id: string, data: Partial<AdminUser>): Promise<AdminUser> => {
        const response = await api.put<AdminUser>(`/admin/users/${id}`, data);
        return response.data;
    },
    deleteUser: async (id: string): Promise<void> => {
        await api.delete(`/admin/users/${id}`);
    },
};
